import { z } from "zod";
import { SystemOfRecordSchema } from "../lib/systemOfRecord";
import { PermissionIdSchema } from "./permission";

export const WorkspaceMemberSchema = z.object({
  id: z.string(),
  userId: z.string(),
  workspaceId: z.string(),
  roleId: z.string(),
  permissions: z.array(PermissionIdSchema),
  createdAt: z.string(),
  acceptedTerms: z.boolean().default(false),
});

export const WorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  auditEnabled: z.boolean(),
  imageExtractionsEnabled: z.boolean(),
  createdAt: z.string(),
  subscriptionStatus: z.enum(["Paid", "Inactive", "Trial"]),
  controlsV2Enabled: z.boolean().optional(),
  authProvider: z.enum(["midship", "workos"]).optional(),
  complexityToggleEnabled: z.boolean().optional(),
  attributeTestColumnsEnabled: z.boolean().optional(),
  systemOfRecord: SystemOfRecordSchema.optional().nullable(),
  // Whether this workspace is a sales sandbox. Hydrated onto the payload by
  // `GET /user/` from `forge.sandboxes`; sandbox membership lives in the
  // database, so the browser cannot work it out for itself. Stamped onto the
  // LaunchDarkly workspace context in `launchDarklyContext`, which is what lets
  // a "sandbox is true" targeting rule apply to client-side flags at all.
  // Defaults to false so a backend that predates the field parses fine, and so
  // an absent value fails closed rather than opting a real workspace in.
  sandbox: z.boolean().optional().default(false),
  // `.catch(null)`: an unrecognised category must not fail the whole workspace
  // parse. It would leave the auth context with a user but no workspace, which
  // RequireAuth reads as "not a member of any workspace" and answers by logging
  // the user out, so a backend that adds a third category (the Phase B backfill,
  // or any later one) would put every member of that workspace in a sign-out
  // loop until the frontend ships. Treating unknown as absent is deliberately
  // fail-open here because the gate is enforced server-side (403), so this
  // schema only drives what the user sees, never whether access is allowed.
  type: z.enum(["partner", "standard"]).nullable().optional().catch(null),
});

export const WorkspaceSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  isPrimary: z.boolean(),
});

export type Workspace = z.infer<typeof WorkspaceSchema>;

export type WorkspaceMember = z.infer<typeof WorkspaceMemberSchema>;

export type WorkspaceSummary = z.infer<typeof WorkspaceSummarySchema>;
